// board.js — 盤面ジオメトリ（純粋モジュール / ブラウザAPI非依存）
// Node からもそのまま import して物理シミュレーションの検証ができる。

export const W = 450;
export const H = 800;

export const BALL_R = 6.5;
export const PEG_R = 4.5;
export const GRAVITY = 0.30;
export const DRAIN_Y = 812;

// 発射レール（外周アーク）
export const ARC = { cx: 225, cy: 250, rOut: 200, rIn: 170 };
// 内側ガイドはここで途切れ、玉は開けた盤面へ放り出される。
// 残っている速度がそのまま「どこまで左へ飛ぶか」＝落下位置になるので発射強度が効く。
export const RAIL_EXIT_DEG = 100;
export const RAIL_X = 395;          // レール内壁の x
export const LAUNCH_POS = { x: 410, y: 770 };

// 発射力 → 初速
export const POWER_MIN = 19.0;
export const POWER_MAX = 30.0;
export const powerToSpeed = (p) => POWER_MIN + (POWER_MAX - POWER_MIN) * Math.max(0, Math.min(1, p));

// ハンドルの目盛り → 発射力。
// 実測では発射力 0.25 付近がほぼ左ルート、0.95 以上でしっかり右ルートに乗る。
// 目盛りをそのまま使うと中間の広い範囲が「どちらつかず」になって左打ち／
// 右打ちの区別がつかないので、両端の効く範囲を引き伸ばして割り当てる。
export const LEFT_ZONE = 0.45;    // これ以下の目盛りは左打ち
export const RIGHT_ZONE = 0.55;   // これ以上の目盛りは右打ち

// 鋸歯状の天井で弾かれる都合上、発射力 0.3〜0.98 のあいだは同じ強さでも
// 左右どちらへ行くかが安定しない（実測で 26〜66% の間を行き来する）。
// そこで目盛りは安定して効く両端だけに割り当て、間は谷として通過させる。
export function dialToPower(dial) {
  const d = Math.max(0, Math.min(1, dial));
  if (d <= LEFT_ZONE) return 0.23 + (d / LEFT_ZONE) * 0.04;              // 0.23〜0.27
  if (d >= RIGHT_ZONE) return 0.99 + ((d - RIGHT_ZONE) / (1 - RIGHT_ZONE)) * 0.01;  // 0.99〜1.00
  const t = (d - LEFT_ZONE) / (RIGHT_ZONE - LEFT_ZONE);
  return 0.27 + t * 0.72;
}

/** 今の目盛りがどちら打ちか */
export const dialSide = (dial) => (dial >= RIGHT_ZONE ? 'right' : dial <= LEFT_ZONE ? 'left' : 'mid');

const D2R = Math.PI / 180;

function arcPts(cx, cy, r, a0, a1, steps) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const a = (a0 + (a1 - a0) * (i / steps)) * D2R;
    pts.push([cx + r * Math.cos(a), cy - r * Math.sin(a)]);
  }
  return pts;
}

// 天井（放出後の外周）。玉は遠心力で外周に張り付いたまま滑るため、単純な
// 円弧だと必ず左端まで運ばれてしまう。内側へ突き出す鋸歯状にして玉を弾き
// 落とす。全体が繋がった一本の折れ線なので、玉が挟まる隙間ができない。
export function canopyPts(a0 = 106, a1 = 180, step = 6, rDeep = 180) {
  const pts = [];
  for (let a = a0; a < a1; a += step) {
    const n = pts.length;
    const r = n % 2 === 0 ? ARC.rOut : rDeep;
    const rad = a * D2R;
    pts.push([ARC.cx + r * Math.cos(rad), ARC.cy - r * Math.sin(rad)]);
  }
  const rad = a1 * D2R;
  pts.push([ARC.cx + ARC.rOut * Math.cos(rad), ARC.cy - ARC.rOut * Math.sin(rad)]);
  return pts;
}

export function railExitPoint() {
  const a = RAIL_EXIT_DEG * D2R;
  return { x: ARC.cx + ARC.rIn * Math.cos(a), y: ARC.cy - ARC.rIn * Math.sin(a) };
}

function chain(pts, opt, out) {
  for (let i = 0; i < pts.length - 1; i++) {
    out.push({
      x1: pts[i][0], y1: pts[i][1],
      x2: pts[i + 1][0], y2: pts[i + 1][1],
      kind: opt.kind, rest: opt.rest, fric: opt.fric,
      thick: opt.thick ?? 2,
      id: opt.id,
    });
  }
  return out;
}

// fric は接触中フレームごとの接線速度の残存率。転がり抵抗になるので
// 低くしすぎると緩斜面で玉が這って止まる。
const WALL = { kind: 'wall', rest: 0.34, fric: 0.96 };
const RAIL = { kind: 'rail', rest: 0.26, fric: 0.995 };
// 天井（放出後の上部外周）。ここだけ摩擦を強くして、玉が滑り台のように
// 左端まで運ばれてしまうのを防ぐ。
const CANOPY = { kind: 'canopy', rest: 0.30, fric: 0.90 };
const PANEL = { kind: 'panel', rest: 0.36, fric: 0.97, thick: 3 };
const CUP = { kind: 'cup', rest: 0.16, fric: 0.80 };

// 液晶パネルの外形（上部は急な山形にして玉が乗らないようにする）。
// 天釘を並べる余地を作るため、パネル上端は y=170 まで下げてある。
export const LCD = { x1: 118, y1: 170, x2: 332, y2: 330 };
const LCD_POLY = [
  [118, 222], [146, 200], [225, 170], [304, 200], [332, 222],
  [332, 306], [306, 330], [144, 330], [118, 306], [118, 222],
];

export const WINDMILLS = [
  { x: 135, y: 378, r: 22, blades: 4, speed: 0.030, dir: 1, phase: 0 },
  { x: 315, y: 378, r: 22, blades: 4, speed: 0.030, dir: -1, phase: Math.PI / 4 },
];

// 入賞口。位置はレイアウト側が持って動かせるので、ここには「中心から見て
// どう広がるか」だけを置く。sensor はセンサー矩形、cup は玉を受ける壁、
// gate はアタッカーの蓋（いずれも中心からの相対座標）。
// 表示名は言語ごとに変わるので、文字ではなく鍵で持つ。
export const POCKET_DEFS = [
  {
    id: 'start', labelKey: 'pocket.start', awardKey: 'start', award: 3,
    pos: { x: 211, y: 472 },
    sensor: { x1: -14, y1: -22, x2: 14, y2: 26 },
    // 壁の頂点を命釘の真下に隠す。壁の角が命釘の横に出ていると上向きの V 字が
    // できて玉がそこに乗ったまま止まってしまうため。
    cup: [[[-15, -22], [-15, 22]], [[15, -22], [15, 22]], [[-15, 22], [15, 22]]],
  },
  {
    id: 'attacker', labelKey: 'pocket.attacker', awardKey: 'attacker', award: 14,
    pos: { x: 368, y: 628 },
    sensor: { x1: -20, y1: -17, x2: 20, y2: 22 },
    cup: [[[-22, -17], [-22, 18]], [[22, -34], [22, 18]], [[-22, 18], [22, 18]]],
    // 蓋は「頂点をレール壁に付けて左へ一方向に下る坂」。山形にすると右端と壁の
    // あいだに上向きの V ができ、そこに玉が溜まる。落ち口は左端の一箇所だけ。
    gate: [[-68, -8], [22, -44]],
  },
  {
    id: 'sideL', labelKey: 'pocket.side', awardKey: 'side', award: 3,
    pos: { x: 66, y: 552 },
    sensor: { x1: -7, y1: -18, x2: 7, y2: 26 },
    cup: [[[-12, -22], [-8, 22]], [[12, -22], [8, 22]], [[-8, 22], [8, 22]]],
  },
  {
    id: 'sideR', labelKey: 'pocket.side', awardKey: 'side', award: 3,
    pos: { x: 150, y: 552 },
    sensor: { x1: -7, y1: -18, x2: 7, y2: 26 },
    cup: [[[-12, -22], [-8, 22]], [[12, -22], [8, 22]], [[-8, 22], [8, 22]]],
  },
];

// ---------------------------------------------------------------- 釘配置

// 既定レイアウトを組むときだけ使う立ち入り禁止円（入賞口と風車のまわり）。
// 自作台では利用者が好きな場所に釘を置けるので、ここは通らない。
const EXCLUSION = [
  { x: 135, y: 378, r: 42 },
  { x: 315, y: 378, r: 42 },
  { x: 211, y: 470, r: 46 },
  { x: 366, y: 610, r: 56 },
  { x: 66, y: 552, r: 40 },
  { x: 150, y: 552, r: 40 },
];

// 隙間が玉の直径をわずかに上回る程度だと、玉が入り込んだ末に食い込みの
// 押し戻しが釣り合って永久に挟まる。余裕をもって通れる幅を必ず確保する。
const PEG_WALL_CLEARANCE = PEG_R + BALL_R * 2 + 9;

function distToSegment(px, py, s) {
  const ax = s.x1, ay = s.y1;
  const bx = s.x2 - ax, by = s.y2 - ay;
  const len2 = bx * bx + by * by;
  let t = len2 > 0 ? ((px - ax) * bx + (py - ay) * by) / len2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (ax + bx * t), py - (ay + by * t));
}

// 命釘の間隔（釘調整）。実機と同じで、ここが数 px 変わるだけで回転率が大きく動く。
// 玉の直径 13px を下回ると入賞できなくなるので下限を設けている。
export const NAIL_GAP_DEFAULT = 15;
const NAIL_GAP_MIN = 13.6;
const NAIL_GAP_MAX = 21;

function buildPegs(segments, nailGap) {
  const pegs = [];

  const placeable = (x, y, type) => {
    if (y < 108 || y > 726) return false;
    // 命釘は入賞口のすぐ上に意図して置くものなので、除外円の対象外
    if (type !== 'guide') {
      for (const e of EXCLUSION) {
        if ((x - e.x) ** 2 + (y - e.y) ** 2 < e.r * e.r) return false;
      }
    }
    // 盤面の外／発射レール内には置かない
    const d = Math.hypot(x - ARC.cx, y - ARC.cy);
    if (y < ARC.cy) {
      if (d > ARC.rOut - 18) return false;
      const ang = Math.atan2(ARC.cy - y, x - ARC.cx) / D2R;
      if (ang >= -6 && ang <= RAIL_EXIT_DEG + 6 && d > ARC.rIn - 15) return false;
    }
    // 命釘（ヘソ・アタッカーの寄せ釘）は意図的に壁へ寄せるので免除
    if (type !== 'guide') {
      for (const s of segments) {
        if (distToSegment(x, y, s) < PEG_WALL_CLEARANCE) return false;
      }
    }
    return true;
  };

  const add = (x, y, type = 'peg') => {
    if (placeable(x, y, type)) pegs.push({ x: round1(x), y: round1(y), type });
  };

  // 天釘。放出された玉は遠心力で天井へ張り付いたまま滑るので、その進路上
  // （天井のすぐ内側）に釘を並べて叩き落とす。残り速度＝発射強度によって
  // どの釘で落とされるかが変わり、これが盤面への振り分けになる。
  for (let a = 114; a <= 162; a += 16) {
    const rad = a * D2R;
    add(ARC.cx + 150 * Math.cos(rad), ARC.cy - 150 * Math.sin(rad), 'sky');
  }
  // 放出口右側の散らし釘
  for (let x = 150; x <= 302; x += 38) add(x, 138);
  for (let x = 120; x <= 332; x += 35) add(x, 152);

  // 左サイドルート
  const leftCols = [[54, 84], [48, 76, 100]];
  for (let i = 0, y = 186; y <= 340; y += 30, i++) for (const x of leftCols[i % 2]) add(x, y);
  // 右サイドルート
  const rightCols = [[364], [354, 376]];
  for (let i = 0, y = 186; y <= 340; y += 30, i++) for (const x of rightCols[i % 2]) add(x, y);

  // 中央下段（風車帯〜ヘソ手前）
  for (let r = 0; r < 5; r++) {
    const y = 356 + r * 25;
    const off = (r % 2) * 20;
    for (let x = 52 + off; x <= 380; x += 40) add(x, y);
  }

  // 命釘。斜めに釘を連ねると隙間が玉の直径をわずかに上回る箇所ができて
  // 玉が挟まるので、ヘソの真上に縦の関門を一対だけ置く。重力が通路方向に
  // かかる縦の関門は詰まらない。
  const half = (nailGap + PEG_R * 2) / 2;
  add(211 - half, 444, 'guide'); add(211 + half, 444, 'guide');

  // ヘソ下〜アタッカー帯
  for (let r = 0; r < 4; r++) {
    const y = 506 + r * 26;
    const off = (r % 2) * 21;
    for (let x = 56 + off; x <= 380; x += 42) add(x, y);
  }

  // 最下段
  for (let r = 0; r < 3; r++) {
    const y = 654 + r * 26;
    const off = (r % 2) * 22;
    for (let x = 60 + off; x <= 372; x += 44) add(x, y);
  }
  return pegs;
}

// ---------------------------------------------------------------- 組み立て

const GATE = { kind: 'gate', rest: 0.30, fric: 0.95, thick: 4, id: 'gate' };

const round1 = (v) => Math.round(v * 10) / 10;

export const clampNailGap = (g) =>
  Math.max(NAIL_GAP_MIN, Math.min(NAIL_GAP_MAX, Number(g) || NAIL_GAP_DEFAULT));

// 触れない骨格。ここを動かせるようにすると盤面として成立しなくなるので、
// エディタでも編集対象から外している。
function frameSegments(out) {
  // 外周
  chain([[25, 700], [25, 250]], WALL, out);
  chain(arcPts(ARC.cx, ARC.cy, ARC.rOut, 0, 106, 38), RAIL, out);      // 発射レール外壁
  chain(canopyPts(), CANOPY, out);                                     // 盤面の天井（鋸歯）
  chain([[425, 250], [425, 806]], RAIL, out);
  chain([[425, 806], [RAIL_X, 806]], WALL, out);

  // 下部ファンネル（アウト口へ）
  chain([[25, 700], [188, 796]], WALL, out);
  chain([[RAIL_X, 700], [262, 796]], WALL, out);

  // 発射レール内壁 → 100°で途切れて盤面へ放出
  chain([[RAIL_X, 806], [RAIL_X, 250]], RAIL, out);
  chain(arcPts(ARC.cx, ARC.cy, ARC.rIn, 0, RAIL_EXIT_DEG, 30), RAIL, out);

  // 液晶パネル
  chain(LCD_POLY, PANEL, out);
  return out;
}

function pocketSegments(def, pos, out) {
  const abs = (line) => line.map(([dx, dy]) => [pos.x + dx, pos.y + dy]);
  for (const line of def.cup) chain(abs(line), CUP, out);
  // アタッカー（大入賞口）の蓋。閉じている間は玉の直径より狭い隙間しか残らず、
  // 開放時だけ左端の落ち口から入る。
  if (def.gate) chain(abs(def.gate), GATE, out);
}

/**
 * 左右のルートを分ける仕切り。これが無いと下で合流してしまい、弱く打っても
 * アタッカーに入って「右打ち」の意味が無くなる。
 * 頭は半円。折れ線の「尖った端点」を作ると、そこに玉が乗って釣り合ったまま
 * 動かなくなる（実測: 全滞留がその 1 点に集中した）。半円なら端点が左右の
 * 側面の上端と一致するので、乗った玉は必ずどちらかへ転げ落ちる。
 */
function separatorSegments(s, out) {
  const r = s.r ?? 8;
  chain([[s.x - r, s.y1], [s.x - r, s.y2]], WALL, out);
  chain([[s.x + r, s.y1], [s.x + r, s.y2]], WALL, out);
  chain(arcPts(s.x, s.y1, r, 180, 0, 10), WALL, out);
}

/** 編集できない骨格。エディタが下敷きとして描くのに使う */
export const FRAME_SEGMENTS = frameSegments([]);

/** レイアウトから当たり判定用の線分をすべて起こす */
export function buildSegments(layout) {
  const out = frameSegments([]);
  for (const d of POCKET_DEFS) pocketSegments(d, layout.pockets?.[d.id] || d.pos, out);
  for (const s of layout.separators || []) separatorSegments(s, out);
  for (const w of layout.walls || []) {
    chain([[w.x1, w.y1], [w.x2, w.y2]], WALL, out);
  }
  return out;
}

/**
 * 既定のレイアウト（釘・壁・仕切り・風車・入賞口の位置）を書き出す。
 * 自作台のエディタはここを出発点にする。
 * @param {object} [opts]
 * @param {number} [opts.nailGap] 命釘の間隔(px)
 */
export function defaultLayout(opts = {}) {
  const layout = {
    pegs: [],
    walls: [],
    // 液晶のすぐ下から始める。ここより上で始めると左右が上で混ざってしまい、
    // 弱く打ってもアタッカーへ回り込めてしまう。
    separators: [{ x: 270, y1: 352, y2: 702, r: 8 }],
    windmills: WINDMILLS.map((w) => ({ ...w })),
    pockets: Object.fromEntries(POCKET_DEFS.map((d) => [d.id, { ...d.pos }])),
  };
  layout.pegs = buildPegs(buildSegments(layout), clampNailGap(opts.nailGap ?? NAIL_GAP_DEFAULT));
  return layout;
}

/**
 * 盤面を組み立てる。
 * @param {object} [opts]
 * @param {number} [opts.nailGap]  命釘の間隔(px)。広いほどヘソに入りやすい
 * @param {object} [opts.awards]   {start, side, attacker} 各入賞口の払い出し
 * @param {object} [opts.layout]   自作台のレイアウト。無ければ既定を組む
 */
export function createBoard(opts = {}) {
  const nailGap = clampNailGap(opts.nailGap ?? NAIL_GAP_DEFAULT);
  const awards = opts.awards || {};
  const layout = opts.layout || defaultLayout({ nailGap });

  return {
    segments: buildSegments(layout),
    nailGap,
    pegs: (layout.pegs || []).map((p) => ({
      x: p.x, y: p.y, r: PEG_R, type: p.type || 'peg', flash: 0,
    })),
    windmills: (layout.windmills || []).map((w) => ({ phase: 0, ...w })),
    pockets: POCKET_DEFS.map((d) => {
      const pos = layout.pockets?.[d.id] || d.pos;
      return {
        id: d.id, labelKey: d.labelKey, cx: pos.x, cy: pos.y,
        x1: pos.x + d.sensor.x1, y1: pos.y + d.sensor.y1,
        x2: pos.x + d.sensor.x2, y2: pos.y + d.sensor.y2,
        award: awards[d.awardKey] ?? d.award,
      };
    }),
    gateOpen: false,
  };
}

// ---------------------------------------------------------------- 編集の補助

/**
 * 盤面として物を置いてよい範囲か。エディタが配置を弾くのに使う。
 * 発射レールの中と、外周・ファンネルの外側は対象外。
 */
export function inPlayfield(x, y) {
  if (y < 104 || y > 782) return false;
  if (y >= ARC.cy) {
    if (x > RAIL_X - 12) return false;                 // 発射レールの中
    let left = 31, right = RAIL_X - 12;
    if (y > 700) {                                     // 下部ファンネル
      const t = (y - 700) / 96;
      left = 31 + (188 - 25) * t;
      right = Math.min(right, 389 - (395 - 262) * t);
    }
    return x > left && x < right;
  }
  // 上部は外周アークの内側。放出前のレールにあたる範囲はさらに内側だけ。
  const d = Math.hypot(x - ARC.cx, y - ARC.cy);
  if (d > ARC.rOut - 14) return false;
  const ang = Math.atan2(ARC.cy - y, x - ARC.cx) / D2R;
  if (ang >= -6 && ang <= RAIL_EXIT_DEG + 6 && d > ARC.rIn - 12) return false;
  return true;
}
